/**
 * This file originates from https://github.com/mariocasciaro/object-path-immutable/blob/master/src/object-path-immutable.js
 * with addition of supporting Symbols as keys
 */
/* eslint-disable */
// @ts-nocheck

import isPlainObject from 'is-plain-object';
import op from 'object-path';
function isEmpty(value) {
  if (isNumber(value)) {
    return false;
  }
  if (!value) {
    return true;
  }
  if (isArray(value) && value.length === 0) {
    return true;
  }
  if (!isString(value)) {
    return Reflect.ownKeys(value).length === 0;
  }
  return false;
}
function isNumber(value) {
  return typeof value === 'number';
}
function isString(obj) {
  return typeof obj === 'string';
}
function isArray(obj) {
  return Array.isArray(obj);
}
function assignToObject(target, source) {
  Reflect.ownKeys(source).forEach(key => {
    target[key] = source[key];
  });
  return target;
}
function getKey(key) {
  const intKey = parseInt(key);
  if (intKey.toString() === key) {
    return intKey;
  }
  return key;
}
function clone(obj, createIfEmpty, assumeArray) {
  if (obj == null) {
    if (createIfEmpty) {
      if (assumeArray) {
        return [];
      }
      return {};
    }
    return obj;
  }
  if (isArray(obj)) {
    return obj.slice();
  }
  return assignToObject({}, obj);
}
function _deepMerge(dest, src) {
  if (dest !== src && isPlainObject(dest) && isPlainObject(src)) {
    const merged = {};
    Reflect.ownKeys(dest).forEach(key => {
      if (Object.hasOwn(src, key)) {
        merged[key] = _deepMerge(dest[key], src[key]);
      } else {
        merged[key] = dest[key];
      }
    });
    Reflect.ownKeys(src).forEach(key => {
      merged[key] = _deepMerge(dest[key], src[key]);
    });
    return merged;
  }
  return src;
}
function _changeImmutable(dest, src, path, changeCallback) {
  if (isNumber(path)) {
    path = [path];
  }
  if (isEmpty(path)) {
    return src;
  }
  if (isString(path)) {
    return _changeImmutable(dest, src, path.split('.').map(getKey), changeCallback);
  }
  const currentPath = path[0];
  if (!dest || dest === src) {
    dest = clone(src, true, isNumber(currentPath));
  }
  if (path.length === 1) {
    return changeCallback(dest, currentPath);
  }
  if (src != null) {
    src = src[currentPath];
  }
  dest[currentPath] = _changeImmutable(dest[currentPath], src, path.slice(1), changeCallback);
  return dest;
}
const api = {};
api.set = function set(dest, src, path, value) {
  if (isEmpty(path)) {
    return value;
  }
  return _changeImmutable(dest, src, path, (clonedObj, finalPath) => {
    clonedObj[finalPath] = value;
    return clonedObj;
  });
};
api.update = function update(dest, src, path, updater) {
  if (isEmpty(path)) {
    return updater(clone(src));
  }
  return _changeImmutable(dest, src, path, (clonedObj, finalPath) => {
    clonedObj[finalPath] = updater(clonedObj[finalPath]);
    return clonedObj;
  });
};
api.push = function push(dest, src, path /* , values */) {
  const values = Array.prototype.slice.call(arguments, 3);
  if (isEmpty(path)) {
    if (!isArray(src)) {
      return values;
    }
    return src.concat(values);
  }
  return _changeImmutable(dest, src, path, (clonedObj, finalPath) => {
    if (!isArray(clonedObj[finalPath])) {
      clonedObj[finalPath] = values;
    } else {
      clonedObj[finalPath] = clonedObj[finalPath].concat(values);
    }
    return clonedObj;
  });
};
api.insert = function insert(dest, src, path, value, at) {
  at = ~~at;
  if (isEmpty(path)) {
    if (!isArray(src)) {
      return [value];
    }
    const first = src.slice(0, at);
    first.push(value);
    return first.concat(src.slice(at));
  }
  return _changeImmutable(dest, src, path, (clonedObj, finalPath) => {
    let arr = clonedObj[finalPath];
    if (!isArray(arr)) {
      if (arr != null && typeof arr !== 'undefined') {
        throw new Error(`Expected ${path}to be an array. Instead got ${typeof path}`);
      }
      arr = [];
    }
    const first = arr.slice(0, at);
    first.push(value);
    clonedObj[finalPath] = first.concat(arr.slice(at));
    return clonedObj;
  });
};
api.del = function del(dest, src, path) {
  if (isEmpty(path)) {
    return undefined;
  }
  return _changeImmutable(dest, src, path, (clonedObj, finalPath) => {
    if (Array.isArray(clonedObj)) {
      if (clonedObj[finalPath] !== undefined) {
        clonedObj.splice(finalPath, 1);
      }
    } else if (Object.hasOwn(clonedObj, finalPath)) {
      delete clonedObj[finalPath];
    }
    return clonedObj;
  });
};
api.assign = function assign(dest, src, path, source) {
  if (isEmpty(path)) {
    if (isEmpty(source)) {
      return src;
    }
    return assignToObject(clone(src), source);
  }
  return _changeImmutable(dest, src, path, (clonedObj, finalPath) => {
    source = Object(source);
    const target = clone(clonedObj[finalPath], true);
    assignToObject(target, source);
    clonedObj[finalPath] = target;
    return clonedObj;
  });
};
api.merge = function assign(dest, src, path, source) {
  if (isEmpty(path)) {
    if (isEmpty(source)) {
      return src;
    }
    return _deepMerge(src, source);
  }
  return _changeImmutable(dest, src, path, (clonedObj, finalPath) => {
    source = Object(source);
    clonedObj[finalPath] = _deepMerge(clonedObj[finalPath], source);
    return clonedObj;
  });
};
export function wrap(src) {
  let dest = src;
  let committed = false;
  var transaction = Object.keys(api).reduce((proxy, prop) => {
    /* istanbul ignore else */
    if (typeof api[prop] === 'function') {
      proxy[prop] = function () {
        const args = [dest, src].concat(Array.prototype.slice.call(arguments));
        if (committed) {
          throw new Error(`Cannot call ${prop} after \`value\``);
        }
        dest = api[prop].apply(null, args);
        return transaction;
      };
    }
    return proxy;
  }, {});
  transaction.value = function () {
    committed = true;
    return dest;
  };
  return transaction;
}
export function get(src, path, defaultValue) {
  return op.get(src, path, defaultValue);
}
export function set(src, path, value) {
  return api.set(null, src, path, value);
}
export function push(src, path, value) {
  return api.push(null, src, path, value);
}
export function del(src, path) {
  return api.del(null, src, path);
}
export function assign(src, path, source) {
  return api.assign(null, src, path, source);
}
export function merge(src, path, source) {
  return api.merge(null, src, path, source);
}
export function update(src, path, updater) {
  return api.update(null, src, path, updater);
}
export function insert(src, path, value, index) {
  return api.insert(null, src, path, value, index);
}